/**
 * Adapts the run contract (columns/measures/stats/groups) to the console
 * kit's prop shapes. Everything here is definition-driven — adding a report
 * on the backend must not require touching this file.
 */
import React from 'react';
import type {
  ConsoleAggregates,
  ConsoleColumnDef,
  ConsoleGroup,
  ConsoleRow,
  ConsoleStat,
} from '@components/ui/console';
import { consoleTokens } from '@components/ui/console';
import { Box, Stack, Typography } from '@mui/material';

import type {
  ReportColumn,
  ReportMeasure,
  ReportStat,
  ReportStatCard,
} from '../types';

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const formatCents = (cents: number): string => usdFormatter.format(cents / 100);

const STATUS_COLORS: Record<string, string> = {
  paid: consoleTokens.green,
  attended: consoleTokens.green,
  submitted: consoleTokens.amber,
  scheduled: consoleTokens.amber,
  denied: consoleTokens.red,
  reversed: consoleTokens.red,
  no_show: consoleTokens.red,
};

const mono = (
  value: React.ReactNode,
  options?: { dim?: boolean; strong?: boolean; color?: string },
): React.ReactNode => (
  <Box
    component="span"
    sx={{
      color: options?.color ?? (options?.dim ? consoleTokens.fainter : consoleTokens.ink),
      fontFamily: consoleTokens.mono,
      fontSize: '0.75rem',
      fontVariantNumeric: 'tabular-nums',
      fontWeight: options?.strong ? 600 : 400,
    }}
  >
    {value}
  </Box>
);

const moneyCell = (value: unknown, strong = false): React.ReactNode => {
  const cents = Number(value ?? 0);
  if (!value && cents === 0) return mono('—', { dim: true });
  if (cents < 0)
    return mono(`−${formatCents(Math.abs(cents))}`, { color: consoleTokens.red, strong });
  return mono(formatCents(cents), { strong });
};

/** Primitive-only display coercion (objects never reach report cells). */
export const asText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const shortDate = (value: unknown): string => {
  const iso = asText(value);
  return iso.length >= 10 ? iso.slice(5, 10).replace('-', '/') : iso;
};

const shortDateTime = (value: unknown): string => {
  const parsed = new Date(asText(value));
  if (Number.isNaN(parsed.getTime())) return asText(value);
  return `${String(parsed.getMonth() + 1).padStart(2, '0')}/${String(
    parsed.getDate(),
  ).padStart(2, '0')} ${String(parsed.getHours()).padStart(2, '0')}:${String(
    parsed.getMinutes(),
  ).padStart(2, '0')}`;
};

const isStatusColumn = (column: ReportColumn): boolean =>
  column.key.endsWith('status') || column.key === 'outcome';

const statusCell = (value: unknown): React.ReactNode => {
  const status = asText(value);
  if (!status) return mono('—', { dim: true });
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box
        sx={{
          bgcolor: STATUS_COLORS[status] ?? consoleTokens.fainter,
          borderRadius: '50%',
          flexShrink: 0,
          height: 6,
          width: 6,
        }}
      />
      <Typography noWrap sx={{ color: consoleTokens.dim, fontSize: '0.6875rem' }}>
        {status.replace(/_/g, ' ')}
      </Typography>
    </Stack>
  );
};

const renderCell = (column: ReportColumn) => {
  if (isStatusColumn(column)) return (row: ConsoleRow) => statusCell(row[column.key]);
  switch (column.type) {
    case 'cents':
      return (row: ConsoleRow) => moneyCell(row[column.key]);
    case 'int':
    case 'decimal':
      return (row: ConsoleRow) => {
        const value = row[column.key];
        if (value === null || value === undefined || value === '')
          return mono('—', { dim: true });
        return mono(String(Math.round(Number(value) * 100) / 100));
      };
    case 'date':
      return (row: ConsoleRow) =>
        row[column.key] ? mono(shortDate(row[column.key]), { dim: true }) : mono('—', { dim: true });
    case 'datetime':
      return (row: ConsoleRow) =>
        row[column.key]
          ? mono(shortDateTime(row[column.key]), { dim: true })
          : mono('—', { dim: true });
    case 'bool':
      return (row: ConsoleRow) => {
        const value = row[column.key];
        if (value === null || value === undefined) return mono('—', { dim: true });
        return mono(value ? 'yes' : '—', { dim: !value });
      };
    default:
      // Render callback, not a component — display-name does not apply.
      // eslint-disable-next-line react/display-name
      return (row: ConsoleRow): React.ReactNode => (
        <Typography noWrap sx={{ color: consoleTokens.dim, fontSize: '0.75rem', pr: 1 }}>
          {asText(row[column.key]) || '—'}
        </Typography>
      );
  }
};

// Minimum content widths; the table's auto layout grows columns to fit and
// a filler column absorbs the leftover slack.
const columnMinWidth = (column: ReportColumn): number => {
  if (isStatusColumn(column)) return 96;
  switch (column.type) {
    case 'cents':
      return 100;
    case 'int':
    case 'decimal':
      return 64;
    case 'date':
      return 80;
    case 'datetime':
      return 110;
    case 'bool':
      return 56;
    default:
      return 96;
  }
};

const renderMeasureAggregate = (measure: ReportMeasure) => {
  if (measure.type === 'cents') {
    return (aggregates: ConsoleAggregates, emphasized: boolean) =>
      moneyCell(aggregates[measure.key], emphasized);
  }
  return (aggregates: ConsoleAggregates, emphasized: boolean) => {
    const value = Number(aggregates[measure.key] ?? 0);
    return mono(String(Math.round(value * 100) / 100), { strong: emphasized });
  };
};

/**
 * Detail columns → console column defs. Columns matching an active group-by
 * dimension are hidden (the band already carries that value); sum-measure
 * aggregates land under the detail column they sum (via source_key).
 */
export const buildConsoleColumns = (
  columns: ReportColumn[],
  measures: ReportMeasure[],
  groupBy: string[],
): ConsoleColumnDef[] => {
  const visible = columns.filter(column => !groupBy.includes(column.key));
  const measureBySource = new Map(
    measures
      .filter(measure => measure.agg === 'sum' && measure.source_key)
      .map(measure => [measure.source_key as string, measure]),
  );

  return visible.map(column => {
    const measure = measureBySource.get(column.key);
    const numeric =
      column.type === 'cents' || column.type === 'int' || column.type === 'decimal';
    return {
      key: column.key,
      label: column.label,
      minWidth: columnMinWidth(column),
      align: numeric ? 'right' : 'left',
      fill: Boolean(column.fill),
      render: renderCell(column),
      renderAggregate: measure ? renderMeasureAggregate(measure) : undefined,
    };
  });
};

const formatStatValue = (value: unknown, format: string): string => {
  const numeric = Number(value ?? 0);
  switch (format) {
    case 'cents':
      return numeric < 0 ? `−${formatCents(Math.abs(numeric))}` : formatCents(numeric);
    case 'decimal':
      return String(Math.round(numeric * 100) / 100);
    default:
      return numeric.toLocaleString('en-US');
  }
};

/** Run-response stats → stat strip props. */
export const buildConsoleStats = (
  stats: ReportStat[],
  deltaTooltip?: string,
): ConsoleStat[] =>
  stats.map(stat => ({
    key: stat.key,
    label: stat.label,
    value: formatStatValue(stat.value, stat.format),
    deltaPct: stat.delta_pct,
    deltaTooltip,
    warning: stat.emphasis === 'warning_if_positive' && Number(stat.value ?? 0) > 0,
  }));

/**
 * Band meta line: row count plus any warning-emphasis stat that is positive
 * for this group (e.g. `24 · 1 unsigned`).
 */
export const buildBandMeta =
  (statCards: ReportStatCard[]) =>
  (group: ConsoleGroup): React.ReactNode => {
    const warnings = statCards
      .filter(card => card.emphasis === 'warning_if_positive')
      .map(card => ({ card, count: Number(group.aggregates[card.measure_key] ?? 0) }))
      .filter(entry => entry.count > 0);
    return [
      String(group.row_count),
      ...warnings.map(entry => `${entry.count} ${entry.card.label}`),
    ].join(' · ');
  };
